import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import { CONTACT_TOPICS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", topic: CONTACT_TOPICS[0], message: "", privacy: false };

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30";

const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      setStatus("success");
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "generate_lead_contact" });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-18465206934/oeSyCMaSi4AdEJbI8uRE" });
      }
      toast.success("Richiesta inviata correttamente. La segreteria ti ricontatterà al più presto.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invio non riuscito. Riprova o contatta lo studio telefonicamente.");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-form-success"
        className="flex flex-col items-center rounded-2xl border border-gold/40 bg-gold-soft p-10 text-center"
      >
        <CheckCircle2 size={44} className="text-gold-dark" aria-hidden="true" />
        <p className="mt-4 font-serif text-2xl font-semibold text-navy">Richiesta inviata</p>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
          Grazie per aver contattato lo studio. La segreteria ti ricontatterà al più presto
          ai recapiti che hai indicato.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-5" noValidate={false}>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-navy">
            Nome e cognome *
          </label>
          <input
            id="contact-name"
            data-testid="contact-name-input"
            type="text"
            required
            minLength={2}
            maxLength={100}
            value={form.name}
            onChange={set("name")}
            className={inputCls}
            placeholder="Il tuo nome"
            autoComplete="name"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-navy">
            Email *
          </label>
          <input
            id="contact-email"
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            className={inputCls}
            placeholder="nome@esempio.it"
            autoComplete="email"
          />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-semibold text-navy">
            Telefono
          </label>
          <input
            id="contact-phone"
            data-testid="contact-phone-input"
            type="tel"
            maxLength={30}
            value={form.phone}
            onChange={set("phone")}
            className={inputCls}
            placeholder="Facoltativo, per essere ricontattato"
            autoComplete="tel"
          />
        </div>
        <div>
          <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-semibold text-navy">
            Argomento *
          </label>
          <select
            id="contact-topic"
            data-testid="contact-topic-select"
            required
            value={form.topic}
            onChange={set("topic")}
            className={inputCls}
          >
            {CONTACT_TOPICS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-navy">
          Messaggio *
        </label>
        <textarea
          id="contact-message"
          data-testid="contact-message-textarea"
          required
          minLength={10}
          maxLength={3000}
          rows={5}
          value={form.message}
          onChange={set("message")}
          className={inputCls}
          placeholder="Descrivi brevemente la tua situazione. Evita di inserire dati particolarmente sensibili: ne parleremo durante l'appuntamento."
        />
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          data-testid="contact-privacy-checkbox"
          required
          checked={form.privacy}
          onChange={set("privacy")}
          className="mt-1 h-4 w-4 shrink-0 accent-gold"
        />
        <span>
          Ho letto la{" "}
          <Link to="/privacy-policy" data-testid="contact-privacy-link" className="font-semibold text-gold-dark underline underline-offset-2">
            Privacy Policy
          </Link>{" "}
          e acconsento al trattamento dei miei dati per essere ricontattato dalla segreteria dello studio. *
        </span>
      </label>
      <button
        type="submit"
        data-testid="contact-form-submit-button"
        disabled={status === "loading"}
        className="btn-gold w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Send size={16} aria-hidden="true" />
        {status === "loading" ? "Invio in corso..." : "Invia la richiesta"}
      </button>
      <p className="text-xs leading-relaxed text-slate-500">
        Inviando il modulo, la tua richiesta viene trasmessa alla segreteria dello studio,
        che ti ricontatterà. Il modulo non costituisce un sistema di prenotazione degli
        appuntamenti.
      </p>
    </form>
  );
};

export default ContactForm;
